/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { EditOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Button from 'src/components/ts-components/Button'
import { useSectionEdit } from '../context/SectionEditContext'
import HeaderCancelIcon from './HeaderCancelIcon'
import HeaderSaveIcon from './HeaderSaveIcon'

interface SectionEditActionsProps {
  sectionKey: string
}

/**
 * Edit, or Cancel + Save while this section is being edited. One section is
 * edited at a time, so Edit is hidden on the others until it finishes. Renders
 * nothing outside a `SectionEditContext` (a form without read mode).
 */
const SectionEditActions: React.FC<SectionEditActionsProps> = ({
  sectionKey,
}) => {
  const sectionEdit = useSectionEdit()
  const { submitForm, resetForm } = useFormikContext()

  if (!sectionEdit) {
    return null
  }

  const { editingSection, startEditing, stopEditing, isSaving } = sectionEdit

  if (editingSection === sectionKey) {
    return (
      <div css={rowStyle}>
        <Button
          text={capitalizeWords('Cancel')}
          variant='outlined'
          size='compact'
          icon={<HeaderCancelIcon size={16} />}
          disabled={isSaving}
          onClick={() => {
            // Only this section can hold edits, so resetting the whole form
            // discards exactly them (and clears any API errors they raised).
            resetForm()
            stopEditing()
          }}
        />
        <Button
          text={capitalizeWords('Save')}
          variant='primary'
          size='compact'
          icon={<HeaderSaveIcon size={16} />}
          disabled={isSaving}
          isLoading={isSaving}
          onClick={submitForm}
        />
      </div>
    )
  }

  if (editingSection) {
    return null
  }

  return (
    <Button
      text={capitalizeWords('Edit')}
      variant='outlined'
      size='compact'
      icon={<EditOutlined />}
      onClick={() => startEditing(sectionKey)}
    />
  )
}

export default SectionEditActions

// ---- styled ----
const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`
